// PostHog Analytics Provider
//
// This provider handles PostHog-specific event tracking for UI interactions,
// funnel events, and user behavior analytics. It migrates logic from the original
// posthog.ts file to work with the new provider architecture.
//
// CROSS-SUBDOMAIN TRACKING:
// This provider includes special handling for form submission events that occur
// on different subdomains. When a user has a conversation on www.example.com and
// then submits a form on start.example.com:
// - Local storage is NOT shared across subdomains
// - Cookies ARE shared (domain=.example.com)
// - The provider falls back to cookies to retrieve lastActiveSessionId and clientId
// - This ensures proper attribution of form submissions to the original conversation
//
// See cross-domain-tracker.ts for full details on the cross-subdomain mechanism.

declare global {
    interface Window {
        __INBOUNDX_POSTHOG_KEY__?: string;
        __INBOUNDX_POSTHOG_HOST__?: string;
    }
}

import { logger } from '../../utils/core/logger';
import { maskId } from '../../utils/core/mask';
import { getStableClientId } from '../../utils/domain/clientId';
import { getCrossDomainTrackingData, isOnDifferentSubdomain } from '../cross-domain-tracker';
import { PageEventMirror } from '../page-event-mirror';
import {
    getPersonId,
    setPostHogInstanceGetter as setPersonIdPostHogInstanceGetter,
} from '../person-id';
import type { AnalyticsContext, AnalyticsEvent } from '../types';
import { EventCategories, EventTypes } from '../types';
import { BaseAnalyticsProvider } from './base';

export const POSTHOG_INIT_BLOB_UNAVAILABLE = 'POSTHOG_INIT_BLOB_UNAVAILABLE';

// Named PostHog instance for Chrome extension to avoid collisions with the host
// page's PostHog (or a Rose widget already on the page using the default instance).
const CHROME_POSTHOG_INSTANCE_NAME = 'rose-chrome';
// Same collision problem in the client-backoffice Playground: the backoffice app
// already initialised the default PostHog instance with its own project key, so
// the widget must use a named instance to report to the Rose project instead.
const BACKOFFICE_PLAYGROUND_POSTHOG_INSTANCE_NAME = 'rose-backoffice-playground';

const EXPERIMENT_SUPER_PROPERTY_KEYS = [
    'rw_staff_experiment_id',
    'rw_staff_experiment_start_date',
    'rw_staff_experiment_assignment',
    'rw_client_experiment_id',
    'rw_client_experiment_start_date',
    'rw_client_experiment_assignment',
    'rw_rose_exposure_variant',
    'rw_rose_exposure_reason',
] as const;

/** Prefix a PostHog event or property name, unless it already carries the prefix. */
function withRwPrefix(name: string): string {
    return name.startsWith('rw_') ? name : `rw_${name}`;
}

/**
 * Property keys are rw_snake_case. Unmapped camelCase keys used to leak through as
 * rw_camelCase (rw_displayMode, rw_ctaLabel, ...), so the same value could arrive
 * under two spellings depending on whether someone added a propertyNameMap entry.
 */
function toRwPropertyKey(key: string): string {
    return withRwPrefix(key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase());
}

class PostHogInitializationError extends Error {
    public readonly code: string;
    public readonly originalError?: Error;

    constructor(code: string, message: string, originalError?: Error) {
        super(`[${code}] ${message}`);
        this.name = 'PostHogInitializationError';
        this.code = code;
        this.originalError = originalError;
    }
}

interface PostHogInstance {
    capture: (event: string, properties?: Record<string, unknown>) => void;
    isFeatureEnabled: (flag: string) => boolean | undefined;
    identify: (distinctId: string, properties?: Record<string, unknown>) => void;
    register: (properties: Record<string, unknown>) => void;
    unregister: (property: string) => void;
    get_distinct_id?: () => string;
    getFeatureFlagPayload?: (flag: string) => unknown;
    __loaded?: boolean;
    persistence?: {
        get_property: (key: string) => unknown;
        set_property: (key: string, value: unknown) => void;
    };
}

/** posthog-js persistence key holding its client-side capture token bucket. */
const CAPTURE_RATE_LIMIT_KEY = '$capture_rate_limit';

/**
 * Repair a poisoned posthog-js client rate-limit bucket (IX-4431).
 *
 * posthog-js persists `{ tokens, last }` and refills with `(now - last) / 1000 * rate`.
 * That refill has an upper clamp but no lower one, so a device clock that moves backwards
 * between page loads drives `tokens` deeply negative — and since it only recovers at
 * 10 tokens per second of wall-clock, a clock that jumped back N days silences every
 * `capture()` for N days (events are dropped outright, not queued).
 */
function healCaptureRateLimitBucket(instance: PostHogInstance): void {
    const persistence = instance.persistence;
    if (!persistence) {
        return;
    }

    const bucket = persistence.get_property(CAPTURE_RATE_LIMIT_KEY) as
        | { tokens?: unknown; last?: unknown }
        | undefined;
    if (!bucket) {
        return;
    }

    const now = Date.now();
    const { tokens, last } = bucket;
    const poisoned =
        (typeof tokens === 'number' && tokens < 0) || (typeof last === 'number' && last > now);
    if (!poisoned) {
        return;
    }

    // Clear the entry rather than writing a token count: posthog-js rebuilds it from its own
    // burst config on the next capture, so we never have to mirror its defaults here.
    logger.warn('🩹 Clearing poisoned PostHog capture rate-limit bucket', { tokens, last, now });
    persistence.set_property(CAPTURE_RATE_LIMIT_KEY, undefined);
}

export interface PostHogProviderOptions {
    /** Overrides posthog-js capture_pageview. Defaults to true for the widget. */
    capturePageview?: boolean;
    /** Overrides posthog-js capture_pageleave. Left unset means posthog's default. */
    capturePageleave?: boolean;
}

export class PostHogProvider extends BaseAnalyticsProvider {
    public readonly name = 'posthog';

    private posthogInstance: PostHogInstance | null = null;
    private isPostHogInitializedEventSent = false;
    private hasCapturedManualPageview = false;
    private readonly options: PostHogProviderOptions;

    constructor(options: PostHogProviderOptions = {}) {
        super();
        this.options = options;
    }

    private isBlobInitializationError(error: unknown): boolean {
        if (!(error instanceof Error)) {
            return false;
        }

        const normalizedMessage = error.message.toLowerCase();
        return (
            normalizedMessage.includes('blob is not a constructor') ||
            normalizedMessage.includes('blob is not a function') ||
            (normalizedMessage.includes('illegal constructor') &&
                normalizedMessage.includes('blob'))
        );
    }

    public eventFilter(event: AnalyticsEvent): boolean {
        // PostHog handles UI interactions, conversions, and system events
        // but NOT conversation tracking (N8N) or evaluations (evaluation provider)
        if (event.category === EventCategories.CONVERSATION) {
            return false;
        }

        if (event.category === EventCategories.EVALUATION) {
            return false;
        }

        // Accept all other categories
        return true;
    }

    public async initialize(context: AnalyticsContext): Promise<void> {
        await super.initialize(context);

        // Initialize PostHog SDK (MUST complete successfully in strict mode)
        await this.initializePostHogSDK(context);

        // STRICT MODE: PostHog must be available after initialization
        if (!this.posthogInstance) {
            const error =
                'PostHog instance not available after initialization - PostHog is required';
            logger.error('❌', error);
            throw new Error(error);
        }

        // Set up the PostHog instance getter for the person-id module
        setPersonIdPostHogInstanceGetter(() => this.posthogInstance);

        // Register global (super) properties on all events
        // This is called both on initial initialization AND when context updates
        // (e.g., when session ID changes), ensuring event context stays in sync
        this.registerGlobalProperties(context);

        // Get personId from PostHog and notify event bus
        const personId = getPersonId();
        if (personId) {
            this.notifyContextUpdate({ personId });
            logger.debug('✅ PersonId available and notified to event bus:', personId);
        }

        // CRITICAL: Get stable clientId from PostHog distinct_id
        // This will throw an error if PostHog is not ready (strict mode)
        const clientId = getStableClientId(this.posthogInstance);
        this.notifyContextUpdate({ clientId });
        logger.debug(
            '✅ ClientId from PostHog distinct_id notified to event bus:',
            maskId(clientId)
        );

        logger.debug('✅ PostHog provider initialized with current context:', {
            sessionId: context.sessionId,
            domain: context.domain,
            personId,
            clientId: maskId(clientId),
        });
    }

    /**
     * Initialize PostHog SDK
     *
     * STRICT MODE: This method will throw an error if PostHog fails to initialize
     * or if the distinct_id is not available within the timeout period (5s).
     */
    private async initializePostHogSDK(context: AnalyticsContext): Promise<void> {
        try {
            // Import posthog dynamically to avoid loading issues
            const { default: posthog } = await import('posthog-js');

            // Get configuration from environment
            const apiKey = this.getPostHogApiKey();
            const apiHost = this.getPostHogHost();

            if (!apiKey) {
                const error = 'PostHog API key not found - PostHog provider is required';
                logger.error('❌', error);
                throw new Error(error);
            }

            const deploymentTarget = context.deploymentTarget || 'unknown';
            const isChromeExtension = deploymentTarget === 'chrome-extension';
            const isBackofficePlayground = deploymentTarget === 'backoffice-playground';

            // Use a named instance whenever the widget runs inside a host app that
            // already owns the default PostHog instance (Chrome extension host page,
            // client-backoffice). Otherwise the widget would piggyback on the host's
            // instance and report to the wrong project.
            // In MAIN world, import('posthog-js') returns the same module as the
            // page's PostHog — the default instance is shared and must not be touched.
            const instanceName = isChromeExtension
                ? CHROME_POSTHOG_INSTANCE_NAME
                : isBackofficePlayground
                  ? BACKOFFICE_PLAYGROUND_POSTHOG_INSTANCE_NAME
                  : undefined;

            // Check if our instance is already initialized
            if (instanceName) {
                // biome-ignore lint/suspicious/noExplicitAny: posthog-js doesn't expose getInstance in its types
                const existing = (posthog as any).getInstance?.(instanceName);
                if (existing?.__loaded) {
                    logger.debug(`✅ PostHog named instance "${instanceName}" already initialized`);
                    this.posthogInstance = existing as PostHogInstance;

                    if (existing.get_distinct_id?.()) {
                        logger.debug(
                            '✅ PostHog distinct_id available:',
                            maskId(existing.get_distinct_id())
                        );
                        healCaptureRateLimitBucket(this.posthogInstance);
                        return;
                    }
                    logger.warn('⚠️ PostHog loaded but distinct_id not yet available, waiting...');
                }
            } else if (posthog.__loaded) {
                logger.debug('✅ PostHog SDK already initialized');
                this.posthogInstance = posthog;

                if (posthog.get_distinct_id?.()) {
                    logger.debug(
                        '✅ PostHog distinct_id available:',
                        maskId(posthog.get_distinct_id())
                    );
                    healCaptureRateLimitBucket(this.posthogInstance);
                    return;
                }
                logger.warn('⚠️ PostHog loaded but distinct_id not yet available, waiting...');
            }

            // Configure PostHog based on deployment target
            const config: Record<string, unknown> = {
                api_host: apiHost,
                // IX-4541: never create person profiles. PostHog bills a person-processed
                // event at up to 4x an anonymous one, and posthog-js makes that state
                // sticky — `_calculate_event_properties` re-registers the `$epp`
                // persistence key on every capture it processes (posthog-core.js:1305-1307),
                // so a device that ever got a profile keeps billing at the higher rate
                // forever. 'never' short-circuits `_hasPersonProcessing()` before
                // persistence is read, which is why no cookie cleanup is needed here.
                // Rose identifies visitors by its own `rw_person_id` event property, and
                // conversation attribution rides on `rw_last_active_session_id` — neither
                // needs a PostHog person. Do not restore 'identified_only'.
                person_profiles: 'never',
                debug: false,
                // Form submissions are tracked explicitly via rw_client_form_submitted,
                // so PostHog DOM autocapture ($autocapture) is redundant — disable it to
                // cut ingestion volume (IX-3879).
                autocapture: false,
                // Enable automatic pageview tracking for behavioral data. Kept as `true`
                // (never 'history_change') on purpose: posthog then fires exactly one
                // $pageview, at init. capturePageviewOnce() below covers SPA navigations
                // that need one after suppression.
                capture_pageview: this.options.capturePageview ?? true,
                disable_session_recording: true, // Disable by default
                // Feature flags are resolved from Supabase config (ConfigResolver), never
                // from PostHog — so the per-load /decide flag fetch is pure waste. Disable it
                // to eliminate the feature-flag request cost (IX-3879).
                advanced_disable_feature_flags: true,
                // IX-3881: stop capturing $web_vitals. It was 17% of PostHog volume, its late
                // flush (median +4min) inflated session duration, and it had no consumer besides
                // the (now capped-gap-sum, web_vitals-excluded) duration estimator.
                capture_performance: { web_vitals: false },
            };

            const pageEventMirror = new PageEventMirror({ apiHost });
            // Install before init because posthog-js creates the initial $pageview during
            // initialization. Page events go only to Rose's first-party endpoint; all other
            // PostHog events pass through unchanged.
            config.before_send = (event: import('posthog-js').CaptureResult | null) =>
                pageEventMirror.beforeSend(event);

            // Only set capture_pageleave when explicitly disabling it. Leaving the key
            // absent preserves posthog's 'if_capture_pageview' default, which couples
            // pageleave to capture_pageview; writing `true` would break that coupling.
            if (this.options.capturePageleave === false) {
                config.capture_pageleave = false;
            }

            // Deployment-specific configurations
            if (isChromeExtension) {
                // Chrome extensions should use localStorage instead of cookies for persistence
                // This ensures distinct_id (used as clientId) persists across page reloads
                config.persistence = 'localStorage';
                config.disable_cookie = false; // Allow PostHog to use its internal storage mechanisms

                // In MAIN world, PostHog patches the DOM (addEventListener, history API,
                // fetch/XHR) for autocapture and pageview tracking. This is invasive on
                // the host page and can cause interference or infinite loops.
                // Disable all automatic capturing — we only need custom event tracking.
                config.autocapture = false;
                config.capture_pageview = false;
            } else if (isBackofficePlayground) {
                // Running inside the backoffice: never auto-capture the host app's DOM
                // (its forms, pageviews) — only the widget's own custom events belong
                // in the Rose project.
                config.autocapture = false;
                config.capture_pageview = false;
            }

            logger.debug('🚀 Initializing PostHog SDK with config:', {
                apiKey: apiKey ? 'Found (***...)' : 'NOT FOUND',
                instanceName: instanceName || 'default',
                ...config,
            });

            // Initialize PostHog
            if (instanceName) {
                // Named instance: always call init — posthog-js handles dedup internally.
                // posthog.init() can return undefined for an already-initialised named instance,
                // so fall back to getInstance, then to the existing reference set earlier.
                const instance = posthog.init(apiKey, config, instanceName);
                const resolved =
                    instance ||
                    // biome-ignore lint/suspicious/noExplicitAny: posthog-js doesn't expose getInstance in its types
                    (posthog as any).getInstance?.(instanceName) ||
                    this.posthogInstance;
                if (!resolved) {
                    throw new Error(
                        `PostHog named instance "${instanceName}" could not be obtained after init`
                    );
                }
                this.posthogInstance = resolved as unknown as PostHogInstance;
            } else if (!posthog.__loaded) {
                posthog.init(apiKey, config);
                this.posthogInstance = posthog;
            } else {
                this.posthogInstance = posthog;
            }

            // CRITICAL: Wait for PostHog to be fully ready with distinct_id
            logger.debug('⏳ Waiting for PostHog distinct_id to be available...');
            const startTime = Date.now();
            const timeout = 5000; // 5 second timeout

            if (!this.posthogInstance) {
                throw new Error(
                    instanceName
                        ? `PostHog named instance "${instanceName}" not available after init`
                        : 'PostHog default instance not available after init'
                );
            }
            const instance = this.posthogInstance;
            healCaptureRateLimitBucket(instance);

            while (!instance.get_distinct_id?.()) {
                if (Date.now() - startTime > timeout) {
                    const error =
                        'PostHog initialization timeout - distinct_id not available after 5s';
                    logger.error('❌', error);
                    throw new Error(error);
                }
                // Wait 50ms before checking again
                await new Promise((resolve) => setTimeout(resolve, 50));
            }

            const distinctId = instance.get_distinct_id?.();
            if (!distinctId) {
                throw new Error(
                    'PostHog initialization invariant violated - distinct_id not available after readiness wait'
                );
            }
            logger.debug(
                '✅ PostHog SDK initialized and ready with distinct_id:',
                maskId(distinctId)
            );
        } catch (error) {
            if (this.isBlobInitializationError(error)) {
                const originalError = error instanceof Error ? error : undefined;
                const classifiedError = new PostHogInitializationError(
                    POSTHOG_INIT_BLOB_UNAVAILABLE,
                    'PostHog unavailable in this runtime: Blob API is not constructable',
                    originalError
                );

                logger.error(
                    '❌ PostHog initialization blocked by runtime Blob incompatibility:',
                    classifiedError,
                    {
                        code: classifiedError.code,
                        deploymentTarget: context.deploymentTarget || 'unknown',
                        userAgent:
                            typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                        originalErrorMessage: originalError?.message || String(error),
                    }
                );

                throw classifiedError;
            }

            logger.error('❌ Failed to initialize PostHog SDK:', error);
            // Re-throw error to propagate failure (strict mode)
            throw error;
        }
    }

    /**
     * Get PostHog API key from environment
     * Supports both Vite (import.meta.env) and Webpack (DefinePlugin globals)
     */
    private getPostHogApiKey(): string | null {
        // Try Webpack DefinePlugin globals first (used by widget)
        if (typeof window !== 'undefined') {
            const webpackKey = window.__INBOUNDX_POSTHOG_KEY__;
            if (webpackKey) {
                return webpackKey;
            }
        }

        // Try Vite environment variables (used by preprod, chrome-plugin)
        // biome-ignore lint/suspicious/noExplicitAny: import.meta.env type varies by bundler
        if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
            // biome-ignore lint/suspicious/noExplicitAny: import.meta.env type varies by bundler
            const viteEnv = (import.meta as any).env;
            return viteEnv.VITE_PUBLIC_POSTHOG_KEY || null;
        }

        return null;
    }

    /**
     * Get PostHog API host from environment
     * Supports both Vite (import.meta.env) and Webpack (DefinePlugin globals)
     */
    private getPostHogHost(): string | null {
        // Try Webpack DefinePlugin globals first (used by widget)
        if (typeof window !== 'undefined') {
            const webpackHost = window.__INBOUNDX_POSTHOG_HOST__;
            if (webpackHost) {
                return webpackHost;
            }
        }

        // Try Vite environment variables (used by preprod, chrome-plugin)
        // biome-ignore lint/suspicious/noExplicitAny: import.meta.env type varies by bundler
        if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
            // biome-ignore lint/suspicious/noExplicitAny: import.meta.env type varies by bundler
            const viteEnv = (import.meta as any).env;
            return viteEnv.VITE_PUBLIC_POSTHOG_HOST || null;
        }

        return null;
    }

    /**
     * Set the PostHog instance (called from React component with usePostHog)
     */
    public setPostHogInstance(posthog: PostHogInstance | null): void {
        this.posthogInstance = posthog;

        if (posthog) {
            logger.debug('✅ PostHog instance set successfully');

            // Set global properties that will be added to ALL events
            this.registerGlobalProperties(this.getContext());

            // Update person-id module
            setPersonIdPostHogInstanceGetter(() => posthog);

            // Get personId and notify event bus
            const personId = getPersonId();
            if (personId) {
                this.notifyContextUpdate({ personId });
                logger.debug('✅ PersonId updated and notified to event bus:', personId);
            }

            // Get stable clientId and notify event bus (for cross-subdomain tracking)
            const clientId = getStableClientId(posthog);
            this.notifyContextUpdate({ clientId });
            logger.debug('✅ ClientId notified to event bus:', maskId(clientId));
        } else {
            logger.debug('❌ PostHog instance cleared');
        }
    }

    /**
     * Override updateContext to also update PostHog global properties
     */
    public override updateContext(context: Partial<AnalyticsContext>): void {
        // Call base implementation to update internal context
        super.updateContext(context);

        // Update PostHog global properties with new context
        if (this.posthogInstance) {
            this.registerGlobalProperties(this.getContext());
        }

        logger.debug('✅ PostHog provider context updated with session:', {
            sessionId: context.sessionId,
            domain: context.domain,
        });
    }

    /**
     * Get PostHog instance with fallback to global scope
     */
    public getPostHog(): PostHogInstance | null {
        // Do NOT fall back to window.posthog. In MAIN world (chrome-extension),
        // window.posthog is the host page's instance, not ours.
        return this.posthogInstance;
    }

    public hasSentPostHogInitializedEvent(): boolean {
        return this.isPostHogInitializedEventSent;
    }

    /**
     * Emit a `$pageview` that posthog-js will not emit on its own.
     *
     * With `capture_pageview: true` posthog fires exactly one $pageview, at init.
     * When the policy suppressed that (out-of-scope host) and the visitor then
     * SPA-navigates onto an in-scope page, nothing would ever restore it — and the
     * batch processor needs it to create the session row. Latched: at most one per
     * page load, so repeated navigations don't inflate the pageview count.
     */
    public capturePageviewOnce(): void {
        // Only meaningful when we suppressed posthog's own init-time $pageview. If we
        // didn't, one already went out and a second would double-count. Self-guarding
        // here keeps callers from having to know the policy.
        if (this.options.capturePageview !== false) {
            return;
        }
        if (this.hasCapturedManualPageview) {
            return;
        }
        const posthog = this.getPostHog();
        if (!posthog) {
            return;
        }
        this.hasCapturedManualPageview = true;
        logger.debug('📊 Capturing deferred $pageview after navigation onto an in-scope page');
        posthog.capture('$pageview', {});
    }

    protected async sendEventImpl(event: AnalyticsEvent): Promise<void> {
        const posthog = this.getPostHog();
        if (!posthog) {
            throw new Error('PostHog instance not available');
        }

        // Check for proper event ordering: rw_posthog_initialize should be sent before rw_widget_impression
        if (event.type === EventTypes.WIDGET_IMPRESSION && !this.isPostHogInitializedEventSent) {
            logger.warn(
                '⚠️ rw_widget_impression is being sent without rw_posthog_initialize sent first. ' +
                    'This may indicate an initialization ordering issue.'
            );
        }

        // Convert analytics event to PostHog format
        const posthogEvent = this.convertToPostHogEvent(event);
        const eventData = this.addCommonProperties(posthogEvent.properties, event);

        logger.debug(`📊 PostHog tracking ${posthogEvent.name}:`, eventData);

        try {
            const isPostHogInitializedEvent = event.type === EventTypes.POSTHOG_INITIALIZED;

            // Check if this is a navigation event that needs robust delivery
            const isNavigationEvent = this.isNavigationEvent(event);

            if (isNavigationEvent) {
                // Use robust tracking for critical navigation events
                await this.trackEventRobustly(posthogEvent.name, eventData);
            } else {
                // Standard tracking for non-navigation events
                posthog.capture(posthogEvent.name, eventData);
                logger.debug(`✅ ${posthogEvent.name} sent to PostHog`);
            }

            // Track rw_posthog_initialized only after successful capture.
            if (isPostHogInitializedEvent) {
                this.isPostHogInitializedEventSent = true;
                logger.debug(
                    '✅ rw_posthog_initialize event sent - initialization tracking enabled'
                );
            }
        } catch (error) {
            logger.error(`Failed to track PostHog event ${posthogEvent.name}:`, error);
            throw error;
        }
    }

    protected async sendEvent(event: AnalyticsEvent): Promise<void> {
        await this.sendEventImpl(event);
    }

    private convertToPostHogEvent(event: AnalyticsEvent): {
        name: string;
        properties: Record<string, unknown>;
    } {
        // Map analytics event types to PostHog event names (with rw_ prefix)
        const eventNameMap: Record<string, string> = {
            [EventTypes.WIDGET_IMPRESSION]: 'rw_widget_impression',
            [EventTypes.TYPED_INTO_INPUT]: 'rw_typed_something_into_input',
            [EventTypes.MESSAGE_SENT]: 'rw_message_sent',
            [EventTypes.CTA_PAGE_DISPLAY]: 'rw_cta_page_display',
            [EventTypes.CTA_RENDERED]: 'rw_cta_rendered',
            [EventTypes.CTA_CLICKED]: 'rw_cta_clicked',
            [EventTypes.CLIENT_CTA_CLICKED]: 'rw_client_cta_clicked',
            [EventTypes.FORM_SUBMITTED]: 'rw_client_form_submitted',
            [EventTypes.FORM_SUBMISSION_FILTERED_OUT]: 'rw_client_form_submission_filtered_out',
            [EventTypes.CLICK_HIDDEN_ZONE]: 'rw_click_hidden_zone',
            [EventTypes.MOUSE_HOVER]: 'rw_mouse_hover',
            [EventTypes.DYNAMIC_QUESTION_CLICKED]: 'rw_dynamic_question_clicked',
            [EventTypes.MINIMIZED_DYNAMIC_QUESTIONS]: 'rw_minimized_dynamic_questions',
            [EventTypes.AI_SECTION_CLICKED]: 'rw_ai_section_clicked',
            [EventTypes.AI_SECTION_DISPLAYED]: 'rw_ai_section_displayed',
            [EventTypes.POSTHOG_INITIALIZED]: 'rw_posthog_initialized',
            [EventTypes.LOGIN_CLICKED]: 'rw_login_clicked',
            [EventTypes.POST_QUALIFICATION_STARTED]: 'rw_post_qualification_started',
            [EventTypes.POST_QUALIFICATION_QUESTION_DISPLAYED]:
                'rw_post_qualification_question_displayed',
            [EventTypes.POST_QUALIFICATION_ANSWER_SUBMITTED]:
                'rw_post_qualification_answer_submitted',
            [EventTypes.POST_QUALIFICATION_COMPLETED]: 'rw_post_qualification_completed',
            [EventTypes.POST_QUALIFICATION_DISMISSED]: 'rw_post_qualification_dismissed',
            [EventTypes.CONTENT_RECOMMENDATION_DISPLAYED]: 'rw_content_recommendation_displayed',
            [EventTypes.CONTENT_RECOMMENDATION_CLICKED]: 'rw_content_recommendation_clicked',
        };

        // A type that already carries the prefix must not get a second one:
        // track('rw_layout_toggle') shipped as rw_rw_layout_toggle for 5 months (IX-4086).
        const posthogEventName = eventNameMap[event.type] || withRwPrefix(event.type);

        // Convert property names to PostHog format (with rw_ prefix)
        const properties = this.convertPropertiesToPostHogFormat(event.properties);

        return {
            name: posthogEventName,
            properties,
        };
    }

    private convertPropertiesToPostHogFormat(
        properties: Record<string, unknown>
    ): Record<string, unknown> {
        const converted: Record<string, unknown> = {};

        // Property name mappings for consistency with existing PostHog events
        const propertyNameMap: Record<string, string> = {
            siteName: 'rw_site_name',
            inputLength: 'rw_character_count',
            isFirstInput: 'rw_first_input',
            messageNumber: 'rw_message_number',
            messageLength: 'rw_character_count',
            message_source: 'rw_message_source',
            conversation_triggered_by: 'rw_conversation_triggered_by',
            pageUrl: 'rw_page_url',
            ctaCount: 'rw_cta_count',
            language: 'rw_language',
            url: 'rw_cta_url',
            label: 'rw_cta_label',
            ctaId: 'rw_cta_id',
            formId: 'rw_form_id',
            formClass: 'rw_form_class',
            formAction: 'rw_form_action',
            formMethod: 'rw_form_method',
            fieldCount: 'rw_field_count',
            detectionMethod: 'rw_detection_method',
            provider: 'rw_form_provider',
            status: 'rw_form_status',
            statusCode: 'rw_form_status_code',
            errorMessage: 'rw_form_error_message',
            indicators: 'rw_form_indicators',
            originalEventName: 'rw_original_event_name',
            filterReason: 'rw_filter_reason',
            filteredOut: 'rw_filtered_out',
            clickMethod: 'rw_click_method',
            hiddenZoneType: 'rw_hidden_zone_type',
            widgetState: 'rw_widget_state',
            viewLayout: 'rw_view_layout',
            questionText: 'rw_question_text',
            questionCount: 'rw_question_count',
            questions: 'rw_questions',
            sectionId: 'rw_section_id',
            source: 'rw_source',
            hasSearchBar: 'rw_has_search_bar',
            isMinimized: 'rw_is_minimized',
            totalQuestions: 'rw_total_questions',
            apiHost: 'rw_initialization_api_host',
            loginUrl: 'rw_login_url',
            // Display decision parameters (for rw_posthog_initialized event)
            displayAllowed: 'rw_display_allowed',
            displayReason: 'rw_display_reason',
            forceDisplay: 'rw_force_display',
            trafficControlPassed: 'rw_traffic_control_passed',
            trafficControlReason: 'rw_traffic_control_reason',
            userBucket: 'rw_user_bucket',
            urlAllowed: 'rw_url_allowed',
            deviceAllowed: 'rw_device_allowed',
            currentUrl: 'rw_current_url',
            isMobile: 'rw_is_mobile',
            mobileEnabled: 'rw_mobile_enabled',
            deviceType: 'rw_device_type',
            userAgent: 'rw_user_agent',
            excludePatterns: 'rw_exclude_patterns',
            // CTA diagnostic properties
            isCtaPage: 'rw_is_cta_page',
            hadDemoOffer: 'rw_had_demo_offer',
            inlineCtaOptionEnabled: 'rw_inline_cta_option_enabled',
            // IMPORTANT: Map deploymentTarget to prevent camelCase version from leaking through
            // Global properties use rw_deployment_target (snake_case), so event properties should too
            deploymentTarget: 'rw_deployment_target',
            // Post-qualification properties
            is_post_conversion: 'rw_is_post_conversion',
            is_form_assistant: 'rw_is_form_assistant',
            questionNumber: 'rw_question_number',
            progressPercent: 'rw_progress_percent',
            answerSource: 'rw_answer_source',
            messageCount: 'rw_message_count',
            // Content recommendation properties
            title: 'rw_title',
            category: 'rw_category',
            is_internal: 'rw_is_internal',
            // Experiment cohort properties are already in PostHog format.
            rw_staff_experiment_id: 'rw_staff_experiment_id',
            rw_staff_experiment_start_date: 'rw_staff_experiment_start_date',
            rw_staff_experiment_assignment: 'rw_staff_experiment_assignment',
            rw_client_experiment_id: 'rw_client_experiment_id',
            rw_client_experiment_start_date: 'rw_client_experiment_start_date',
            rw_client_experiment_assignment: 'rw_client_experiment_assignment',
            rw_rose_exposure_variant: 'rw_rose_exposure_variant',
            rw_rose_exposure_reason: 'rw_rose_exposure_reason',
            rw_experiment_excluded_reason: 'rw_experiment_excluded_reason',
        };

        for (const [key, value] of Object.entries(properties)) {
            const mappedKey = propertyNameMap[key] || toRwPropertyKey(key);
            converted[mappedKey] = value;
        }

        return converted;
    }

    private buildGlobalProperties(context: AnalyticsContext): Record<string, unknown> {
        const globalProperties: Record<string, unknown> = {};

        if (context.domain) {
            globalProperties.rw_domain = context.domain;
        }
        if (context.rootDomain) {
            globalProperties.rw_root_domain = context.rootDomain;
        }
        if (context.sessionId) {
            globalProperties.rw_session_id = context.sessionId;
        }
        if (context.lastActiveSessionId) {
            globalProperties.rw_last_active_session_id = context.lastActiveSessionId;
        }
        if (context.lastActiveSessionDate) {
            globalProperties.rw_last_active_session_date = context.lastActiveSessionDate;
        }
        if (context.clientId) {
            globalProperties.rw_client_id = context.clientId;
        }
        if (context.conversation_triggered_by) {
            globalProperties.rw_conversation_triggered_by = context.conversation_triggered_by;
        }
        if (context.personId) {
            globalProperties.rw_person_id = context.personId;
        }
        if (context.apiVersion) {
            globalProperties.rw_api_version = context.apiVersion;
        }
        if (context.frontendVersion) {
            globalProperties.rw_frontend_version = context.frontendVersion;
        }
        if (context.deploymentTarget) {
            globalProperties.rw_deployment_target = context.deploymentTarget;
        }
        if (context.environment) {
            globalProperties.rw_environment = context.environment;
        }
        if (context.gitCommitSha) {
            globalProperties.rw_git_commit_sha = context.gitCommitSha;
        }
        if (context.rw_staff_experiment_id) {
            globalProperties.rw_staff_experiment_id = context.rw_staff_experiment_id;
        }
        if (context.rw_staff_experiment_start_date) {
            globalProperties.rw_staff_experiment_start_date =
                context.rw_staff_experiment_start_date;
        }
        if (context.rw_staff_experiment_assignment) {
            globalProperties.rw_staff_experiment_assignment =
                context.rw_staff_experiment_assignment;
        }
        if (context.rw_client_experiment_id) {
            globalProperties.rw_client_experiment_id = context.rw_client_experiment_id;
        }
        if (context.rw_client_experiment_start_date) {
            globalProperties.rw_client_experiment_start_date =
                context.rw_client_experiment_start_date;
        }
        if (context.rw_client_experiment_assignment) {
            globalProperties.rw_client_experiment_assignment =
                context.rw_client_experiment_assignment;
        }
        if (context.rw_rose_exposure_variant) {
            globalProperties.rw_rose_exposure_variant = context.rw_rose_exposure_variant;
        }
        if (context.rw_rose_exposure_reason) {
            globalProperties.rw_rose_exposure_reason = context.rw_rose_exposure_reason;
        }

        return globalProperties;
    }

    private registerGlobalProperties(context: AnalyticsContext): void {
        const posthog = this.getPostHog();
        if (!posthog) return;

        const globalProperties = this.buildGlobalProperties(context);
        if (Object.keys(globalProperties).length > 0) {
            logger.debug('🌐 Setting global properties for PostHog events:', globalProperties);
            posthog.register(globalProperties);
        }

        // Experiment cohort properties are persisted by PostHog's register() (super properties
        // live in localStorage). Without explicit cleanup they survive past an experiment's
        // end_date and corrupt post-experiment cohort filters. Unregister any experiment key
        // not present in the current context.
        for (const key of EXPERIMENT_SUPER_PROPERTY_KEYS) {
            if (!(key in globalProperties)) {
                posthog.unregister(key);
            }
        }
    }

    private addCommonProperties(
        properties: Record<string, unknown>,
        event?: AnalyticsEvent
    ): Record<string, unknown> {
        const context = this.getContext();
        const globalProps = this.buildGlobalProperties(context);

        // Special handling for FORM_SUBMITTED events: Apply cross-subdomain tracking fallback
        // This ensures forms submitted on different subdomains (e.g., start.example.com)
        // can still be attributed to conversations that started on another subdomain (e.g., www.example.com)
        if (
            event?.type === EventTypes.FORM_SUBMITTED ||
            event?.type === EventTypes.FORM_SUBMISSION_FILTERED_OUT
        ) {
            // Check if lastActiveSessionId is missing - this indicates we're likely on a different subdomain
            const missingSessionId = !context.lastActiveSessionId;

            if (missingSessionId) {
                // Fall back to cookies (which ARE shared across subdomains)
                // When lastActiveSessionId is missing, we replace BOTH sessionId and clientId from cookies
                // to ensure all attribution data comes from the same source (cookies) for consistency
                const crossDomainData = getCrossDomainTrackingData();

                if (crossDomainData.lastActiveSessionId) {
                    globalProps.rw_last_active_session_id = crossDomainData.lastActiveSessionId;
                }

                if (crossDomainData.lastActiveSessionDate) {
                    globalProps.rw_last_active_session_date = crossDomainData.lastActiveSessionDate;
                }

                if (crossDomainData.clientId) {
                    globalProps.rw_client_id = crossDomainData.clientId;
                }

                // Log when cross-domain tracking is used
                if (crossDomainData.lastActiveSessionId || crossDomainData.clientId) {
                    logger.debug('🌍 Using cross-domain tracking data from cookies', {
                        sessionId: crossDomainData.lastActiveSessionId
                            ? maskId(crossDomainData.lastActiveSessionId)
                            : 'none',
                        sessionDate: crossDomainData.lastActiveSessionDate
                            ? new Date(crossDomainData.lastActiveSessionDate).toISOString()
                            : 'none',
                        clientId: crossDomainData.clientId
                            ? maskId(crossDomainData.clientId)
                            : 'none',
                        onDifferentSubdomain: isOnDifferentSubdomain(context.domain),
                    });
                }
            }
        }

        return {
            rw_timestamp: Date.now(),
            ...globalProps,
            ...properties,
        };
    }

    private isNavigationEvent(event: AnalyticsEvent): boolean {
        // Events that typically trigger navigation and need robust delivery
        const navigationEvents: string[] = [
            EventTypes.CTA_CLICKED,
            EventTypes.CLIENT_CTA_CLICKED, // IX-3617: client CTA click navigates away — needs sendBeacon
            EventTypes.CTA_PAGE_DISPLAY, // Added: tracked at click time with source='cta_click'
            EventTypes.FORM_SUBMITTED,
            EventTypes.FORM_SUBMISSION_FILTERED_OUT,
            EventTypes.LOGIN_CLICKED,
        ];

        return navigationEvents.includes(event.type);
    }

    private async trackEventRobustly(
        eventName: string,
        properties: Record<string, unknown>
    ): Promise<void> {
        const posthog = this.getPostHog();
        if (!posthog) {
            logger.debug(`PostHog not available - ${eventName} event lost`);
            return;
        }

        logger.debug(`📊 Robust tracking ${eventName}:`, properties);

        try {
            // biome-ignore lint/suspicious/noExplicitAny: posthog-js capture method typing requires any for transport options
            const posthogAny = posthog as any;

            // IX-2335 fix: Navigation events (FORM_SUBMITTED, CTA_CLICKED, etc.)
            // can race with subsequent page navigations (e.g., Unbounce redirect
            // to thank-you page after AJAX submit). The default XHR transport
            // races against page teardown — Unbounce's redirect tears down the
            // page faster than the SDK's batch flush. Use `transport: 'sendBeacon'`
            // explicitly: the Beacon API is purpose-built for "send-and-leave"
            // scenarios and the browser keeps the request alive even after
            // navigation. Combine with `send_instantly` to bypass batching.
            posthogAny.capture(eventName, properties, {
                send_instantly: true,
                transport: 'sendBeacon',
            });

            // Delay gives the SDK fetch time to flush for callers that await
            await new Promise((resolve) => setTimeout(resolve, 400));
        } catch (error) {
            logger.warn(`Tracking failed for ${eventName}:`, error);
            throw error;
        }
    }

    /**
     * Synchronously capture a navigation-critical event via sendBeacon, called from
     * inside a click handler BEFORE the browser navigates. For a real-link CTA click
     * we cannot await the async event-bus the way the widget's own CTA button does —
     * the browser navigates immediately, and the deferred capture can be torn down
     * before it runs (same-tab or cross-origin). navigator.sendBeacon is dispatched
     * synchronously here and the browser keeps it alive across the teardown.
     * Fire-and-forget; never throws on the host page. Registered super-properties
     * (rw_session_id, rw_domain, …) are attached automatically by posthog.capture.
     */
    public captureNavigationEventSync(type: string, properties: Record<string, unknown>): void {
        const posthog = this.getPostHog();
        if (!posthog) return;
        try {
            const event: AnalyticsEvent = {
                type,
                properties,
                context: this.getContext(),
                timestamp: Date.now(),
            };
            const posthogEvent = this.convertToPostHogEvent(event);
            const eventData = this.addCommonProperties(posthogEvent.properties, event);
            logger.debug(`📡 Beacon tracking ${posthogEvent.name}:`, eventData);
            // biome-ignore lint/suspicious/noExplicitAny: capture transport option isn't in posthog-js types
            (posthog as any).capture(posthogEvent.name, eventData, {
                send_instantly: true,
                transport: 'sendBeacon',
            });
        } catch {
            /* never surface Rose tracking errors on the host page */
        }
    }

    /**
     * Utility methods for external access
     */
    public isPostHogAvailable(): boolean {
        return this.getPostHog() !== null;
    }
}
